// ProjectsPage — dedicated Projects catalog view.
//
// Presents project-type posts in a responsive grid, distinct from the
// chronological hub feed. Matches the Projects view in
// design/project/CitiDev Hub.html and design/project/scripts/pages.jsx.
//
// Design rules (AI_LAB_DESIGN_PRINCIPLES.md):
// - 4px grid, semantic color tokens (--hub-type-project, --surface, etc.)
// - Full interactive-state coverage (hover, focus-visible)
// - Light / dark mode via CSS var() tokens
// - PRD §6 breakpoints: 1-col mobile, 2-col tablet (640px+), 3-col desktop (1024px+)
//
// Navigating to a project's detail page:
// Each project card calls onSelectProject(id) so the parent shell can
// route to the post detail view (AC3). The component is presentation-only;
// routing is the caller's responsibility so this component stays testable
// without a router context.

import { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/api-client";
import { copyText } from "@/lib/clipboard";
import { isSafeHttpUrl } from "@/lib/url";
import { ComposePost } from "@/features/posting-engagement";
import { Avatar } from "@/features/hub-feed/design-atoms";
import { useAuthors, type AuthorMap } from "@/features/hub-feed/useAuthors";
import "./projects-page.css";

const PROJECT_STATUS_LABELS = {
  poc: "POC",
  pilot: "Pilot",
  production: "Production",
} as const;

type ProjectStatus = keyof typeof PROJECT_STATUS_LABELS;

// ---------------------------------------------------------------------------
// Types — mirror api/schemas/projects.py (NOT hand-edited api-types.ts)
// ---------------------------------------------------------------------------

export interface ProjectItem {
  id: string;
  type: "project";
  author_id: string | null;
  division: string;
  title: string;
  excerpt: string;
  tags: string[];
  vote_count: number;
  pinned: boolean;
  created_at: string; // ISO 8601
  /** Set only when the project's content was edited after creation. */
  updated_at?: string | null;
  /** Lifecycle stage: poc | pilot | production. */
  status?: ProjectStatus | null;
  /** What kind of thing this project showcases; null if unset. */
  project_type: string | null;
  /** Whether the author opted this project into the main Hub Feed. */
  posted_to_feed: boolean;
  repo_url: string | null;
  /** Rideshare platform link — set when the project ships on Rideshare. */
  rideshare_url?: string | null;
}

export interface EmptyState {
  headline: string;
  body: string;
}

export interface ProjectsListResponse {
  items: ProjectItem[];
  empty_state: EmptyState | null;
  total: number;
}

// ---------------------------------------------------------------------------
// API fetch
// ---------------------------------------------------------------------------

async function fetchProjects(): Promise<ProjectsListResponse> {
  return apiClient<ProjectsListResponse>("/api/v1/projects");
}

// ---------------------------------------------------------------------------
// Project type color map
// ---------------------------------------------------------------------------

const PROJECT_TYPE_COLOR: Record<string, string> = {
  Skill: "var(--nfs, #0f7d87)",
  "MCP Server": "var(--ss, #bd1f6a)",
  Plugin: "#4d7c0f",
  Automation: "var(--nds, #5b2d8e)",
  Application: "var(--nbs, #1a6fa3)",
};

// Canonical order for the project-type filter row (matches PROJECT_TYPE_LABELS
// in ComposePost). The filter only shows types that are actually present.
const PROJECT_TYPE_ORDER = ["Skill", "MCP Server", "Plugin", "Automation", "Application"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ---------------------------------------------------------------------------
// ProjectCard
// ---------------------------------------------------------------------------

interface ProjectCardProps {
  project: ProjectItem;
  onSelect: (id: string) => void;
  authors?: AuthorMap;
}

function ProjectCard({ project, onSelect, authors = {} }: ProjectCardProps) {
  const typeColor = project.project_type
    ? (PROJECT_TYPE_COLOR[project.project_type] ?? "var(--ink-2, #44473f)")
    : null;
  const owner = project.author_id ? authors[project.author_id] : undefined;
  const ownerName = owner?.name ?? project.author_id ?? "Unknown";

  return (
    <article
      className="projects-card"
      data-testid="project-card"
      data-post-id={project.id}
      aria-label={project.title}
      onClick={() => onSelect(project.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(project.id);
        }
      }}
      role="button"
      tabIndex={0}
    >
      {/* Card header: pin badge + title */}
      <div className="projects-card__header">
        {project.pinned && (
          <span className="projects-card__pin" aria-label="Pinned project">
            {/* Pin icon */}
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </span>
        )}
        <h3 className="projects-card__title">{project.title}</h3>
      </div>

      {/* Owner byline */}
      <div className="projects-card__owner" data-testid="project-card-owner">
        <Avatar id={project.author_id ?? "anon"} initials={owner?.initials} name={owner?.name} avatar={owner?.avatar} size={20} />
        <span>{ownerName}</span>
      </div>

      {/* Excerpt */}
      <p className="projects-card__excerpt">{project.excerpt}</p>

      {/* Tags */}
      {project.tags.length > 0 && (
        <div className="projects-card__tags" aria-label="Tags">
          {project.tags.map((tag) => (
            <span key={tag} className="hub-tag">
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* GitHub repo actions — open or copy the clone command. Clicks here
          must not bubble up to the card's select handler. Only render the link
          for http(s) URLs so a javascript:/data: repo_url can't execute. */}
      {isSafeHttpUrl(project.repo_url) && (
        <div className="projects-card__repo" aria-label="Repository links">
          <a
            href={project.repo_url}
            target="_blank"
            rel="noopener noreferrer"
            className="projects-card__repo-link"
            onClick={(e) => e.stopPropagation()}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 .5C5.7.5.5 5.7.5 12c0 5.1 3.3 9.4 7.9 10.9.6.1.8-.2.8-.5v-1.7c-3.2.7-3.9-1.5-3.9-1.5-.5-1.3-1.3-1.7-1.3-1.7-1.1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.8 1.3 3.5 1 .1-.8.4-1.3.8-1.6-2.6-.3-5.3-1.3-5.3-5.7 0-1.3.5-2.3 1.2-3.1-.1-.3-.5-1.5.1-3.1 0 0 1-.3 3.3 1.2a11.5 11.5 0 0 1 6 0C17.3 4.8 18.3 5.1 18.3 5.1c.6 1.6.2 2.8.1 3.1.8.8 1.2 1.8 1.2 3.1 0 4.4-2.7 5.4-5.3 5.7.4.4.8 1.1.8 2.2v3.3c0 .3.2.6.8.5 4.6-1.5 7.9-5.8 7.9-10.9C23.5 5.7 18.3.5 12 .5z" />
            </svg>
            Open on GitHub
          </a>
          <button
            type="button"
            className="projects-card__repo-clone"
            title={`git clone ${project.repo_url}`}
            onClick={(e) => {
              e.stopPropagation();
              void copyText(`git clone ${project.repo_url}`);
            }}
          >
            Copy clone command
          </button>
        </div>
      )}

      {/* Rideshare link — for city devs who ship on the Rideshare platform
          rather than GitHub. Same http(s)-only guard as the repo link. */}
      {isSafeHttpUrl(project.rideshare_url) && (
        <div className="projects-card__repo" aria-label="Rideshare link">
          <a
            href={project.rideshare_url ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="projects-card__repo-link"
            onClick={(e) => e.stopPropagation()}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              {/* car glyph — NBS Rideshare's ride-share metaphor (Passenger/Driver) */}
              <path d="M5 11l1.5-4.5A2 2 0 0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11" />
              <path d="M3 11h18v5a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1H6v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
              <circle cx="7.5" cy="14" r="0.6" fill="currentColor" />
              <circle cx="16.5" cy="14" r="0.6" fill="currentColor" />
            </svg>
            Open on Rideshare
          </a>
        </div>
      )}

      {/* Footer: type badge, status, vote count, timestamp */}
      <div className="projects-card__footer">
        {project.project_type && (
          <span
            className="projects-card__type-badge"
            style={{ color: typeColor ?? undefined }}
            aria-label={`Type: ${project.project_type}`}
          >
            {project.project_type}
          </span>
        )}

        {project.status && (
          <span
            className={`projects-card__status projects-card__status--${project.status}`}
            aria-label={`Status: ${PROJECT_STATUS_LABELS[project.status]}`}
          >
            {PROJECT_STATUS_LABELS[project.status]}
          </span>
        )}

        <span className="projects-card__votes" aria-label={`${project.vote_count} votes`}>
          ▲ {project.vote_count}
        </span>

        <span className="projects-card__time">
          {project.updated_at ? `Updated ${timeAgo(project.updated_at)}` : timeAgo(project.created_at)}
        </span>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// ProjectsEmptyState
// ---------------------------------------------------------------------------

interface EmptyStateProps {
  emptyState: EmptyState | null;
}

function ProjectsEmptyState({ emptyState }: EmptyStateProps) {
  const headline = emptyState?.headline ?? "No projects here yet";
  const body =
    emptyState?.body ??
    "Share a project to showcase your work and help others build on your ideas.";

  return (
    <div
      className="projects-empty"
      data-testid="projects-empty-state"
      role="status"
      aria-live="polite"
    >
      <div className="projects-empty__icon" aria-hidden="true">
        {/* Grid/box icon matching design empty states */}
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      </div>
      <p className="projects-empty__headline">{headline}</p>
      <p className="projects-empty__body">{body}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProjectsPage
// ---------------------------------------------------------------------------

interface ProjectsPageProps {
  /** Called when the user selects a project card — parent routes to post detail. */
  onSelectProject?: (postId: string) => void;
}

export function ProjectsPage({ onSelectProject }: ProjectsPageProps) {
  const [data, setData] = useState<ProjectsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [activeType, setActiveType] = useState<string | null>(null);
  const authors = useAuthors();

  // Close the compose modal on Escape.
  useEffect(() => {
    if (!composeOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setComposeOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [composeOpen]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchProjects()
      .then((result) => {
        setData(result);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSelect = useCallback(
    (id: string) => {
      if (onSelectProject) {
        onSelectProject(id);
      }
    },
    [onSelectProject],
  );

  // Project-type filter facet: only the types actually present, in canonical
  // order, so the row never offers an empty bucket.
  const availableTypes = useMemo(() => {
    const present = new Set(
      (data?.items ?? []).map((p) => p.project_type).filter((t): t is string => !!t),
    );
    return PROJECT_TYPE_ORDER.filter((t) => present.has(t));
  }, [data]);

  const visibleProjects = useMemo(() => {
    const items = data?.items ?? [];
    return activeType ? items.filter((p) => p.project_type === activeType) : items;
  }, [data, activeType]);

  return (
    <section
      className="projects-page"
      data-testid="projects-page"
      aria-label="Projects catalog"
    >
      <div className="projects-page__head">
        <div>
          <div className="projects-page__eyebrow">Walk the halls of past missions</div>
          <h2 className="projects-page__heading">Projects</h2>
        </div>
        <button
          type="button"
          className="hub-newpost"
          data-testid="projects-add"
          onClick={() => setComposeOpen(true)}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Project
        </button>
      </div>

      {loading && (
        <div className="projects-loading" role="status" aria-live="polite">
          Loading projects…
        </div>
      )}

      {error && !loading && (
        <div className="projects-error" role="alert">
          <strong>Could not load projects.</strong>
          <p>{error.message}</p>
        </div>
      )}

      {data && !loading && (
        <>
          {data.items.length === 0 ? (
            <ProjectsEmptyState emptyState={data.empty_state} />
          ) : (
            <>
              {availableTypes.length > 0 && (
                <div
                  className="projects-type-filter"
                  role="group"
                  aria-label="Filter by project type"
                  data-testid="projects-type-filter"
                >
                  {availableTypes.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={
                        "projects-type-filter__pill" +
                        (activeType === t ? " projects-type-filter__pill--active" : "")
                      }
                      aria-pressed={activeType === t}
                      onClick={() => setActiveType((current) => (current === t ? null : t))}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
              <div
                className="projects-grid"
                data-testid="projects-grid"
                aria-label="Projects grid"
              >
                {visibleProjects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onSelect={handleSelect}
                    authors={authors}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Add Project modal — reuses the shared ComposePost form, preset to
          the "project" post type. */}
      {composeOpen && (
        <div
          className="hub-modal-overlay"
          data-testid="projects-compose-modal"
          onClick={() => setComposeOpen(false)}
        >
          <div
            className="hub-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Add a project"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="hub-modal__head">
              <h2 className="hub-modal__title">Add Project</h2>
              <button
                type="button"
                className="hub-modal__close"
                aria-label="Close"
                onClick={() => setComposeOpen(false)}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <ComposePost
              defaultType="project"
              lockType
              onSuccess={() => {
                setComposeOpen(false);
                load();
              }}
            />
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// CSS: projects-page.css (same directory)
// Projects grid styles: responsive breakpoints per PRD §6, light/dark mode.
// ---------------------------------------------------------------------------
